import React, { useState, useEffect, useRef } from 'react';
import { 
  MessageCircle, Send, User, Clock, CheckCircle, AlertCircle, 
  ArrowLeft, RefreshCw, Mail, Search
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MessagingDashboard({ token }) {
  const [conversations, setConversations] = useState({ to_handle: [], handled: [] });
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState('to_handle');
  const messagesEndRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchConversations = async () => {
    try {
      const res = await axios.get(`${API}/admin/messages/conversations`, { headers });
      setConversations(res.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (userId) => {
    try {
      const res = await axios.get(`${API}/admin/messages/${userId}`, { headers });
      setMessages(res.data.messages);
      setSelectedUser(res.data.user);
      // Refresh conversations to update unread count
      fetchConversations();
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error("Erreur lors du chargement des messages");
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedUser) return;
    setSending(true);
    try {
      await axios.post(`${API}/admin/messages/${selectedUser.id}`, 
        { content: newMessage }, 
        { headers }
      );
      setNewMessage('');
      fetchMessages(selectedUser.id);
      toast.success("Message envoyé");
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error("Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    fetchConversations();
    // Refresh every 30 seconds
    const interval = setInterval(fetchConversations, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) {
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const totalUnread = conversations.to_handle.reduce((sum, c) => sum + c.unread_count, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-navy/50" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-200px)] flex gap-4">
      {/* Conversations List */}
      <div className="w-1/3 flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-bold text-navy flex items-center gap-2">
            <MessageCircle className="w-5 h-5" />
            Messagerie
            {totalUnread > 0 && (
              <Badge className="bg-red-500 text-white">{totalUnread}</Badge>
            )}
          </h3>
          <Button variant="ghost" size="sm" onClick={fetchConversations}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          <Button
            variant={activeTab === 'to_handle' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('to_handle')}
            className={activeTab === 'to_handle' ? 'bg-red-500 hover:bg-red-600' : ''}
          >
            <AlertCircle className="w-4 h-4 mr-1" />
            À répondre ({conversations.to_handle.length})
          </Button>
          <Button
            variant={activeTab === 'handled' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('handled')}
            className={activeTab === 'handled' ? 'bg-emerald-500 hover:bg-emerald-600' : ''}
          >
            <CheckCircle className="w-4 h-4 mr-1" />
            Traités ({conversations.handled.length})
          </Button>
        </div>

        {/* Conversation List */}
        <div className="flex-1 overflow-y-auto space-y-2">
          {(activeTab === 'to_handle' ? conversations.to_handle : conversations.handled).length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              {activeTab === 'to_handle' ? 'Aucun message à traiter' : 'Aucune conversation'}
            </div>
          ) : (
            (activeTab === 'to_handle' ? conversations.to_handle : conversations.handled).map((conv) => (
              <Card 
                key={conv.user_id}
                className={`cursor-pointer transition-all hover:shadow-md ${
                  selectedUser?.id === conv.user_id ? 'ring-2 ring-gold' : ''
                } ${conv.unread_count > 0 ? 'border-red-300 bg-red-50' : ''}`}
                onClick={() => fetchMessages(conv.user_id)}
              >
                <CardContent className="p-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-full bg-navy/10 flex items-center justify-center">
                        <User className="w-5 h-5 text-navy" />
                      </div>
                      <div>
                        <p className="font-medium text-navy text-sm">{conv.user_name || 'Client'}</p>
                        <p className="text-xs text-muted-foreground">{conv.user_email}</p>
                      </div>
                    </div>
                    {conv.unread_count > 0 && (
                      <Badge className="bg-red-500 text-white text-xs">{conv.unread_count}</Badge>
                    )}
                  </div>
                  {conv.last_message && (
                    <p className="text-xs text-muted-foreground mt-2 truncate">
                      {conv.last_sender === 'expert' ? '↩️ Vous: ' : ''}
                      {conv.last_message}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatDate(conv.last_message_at)}
                  </p>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col border rounded-xl bg-white">
        {selectedUser ? (
          <>
            {/* Header */}
            <div className="p-4 border-b bg-navy/5 rounded-t-xl flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="sm" onClick={() => setSelectedUser(null)}>
                  <ArrowLeft className="w-4 h-4" />
                </Button>
                <div className="w-10 h-10 rounded-full bg-navy flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-navy">{selectedUser.name || 'Client'}</p>
                  <p className="text-xs text-muted-foreground">{selectedUser.email}</p>
                </div>
              </div>
              <Badge className="bg-purple-100 text-purple-700">
                {selectedUser.subscription_type?.toUpperCase() || 'MASTER'}
              </Badge>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  Aucun message dans cette conversation
                </div>
              ) : (
                messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender_type === 'expert' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                        msg.sender_type === 'expert'
                          ? 'bg-navy text-white rounded-br-sm'
                          : 'bg-gray-100 text-navy rounded-bl-sm'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      <p className={`text-xs mt-1 ${
                        msg.sender_type === 'expert' ? 'text-white/60' : 'text-muted-foreground'
                      }`}>
                        {formatDate(msg.created_at)}
                      </p>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t bg-gray-50 rounded-b-xl">
              <div className="flex gap-2">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Écrivez votre réponse..."
                  className="flex-1"
                  onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                />
                <Button onClick={sendMessage} disabled={sending || !newMessage.trim()}>
                  <Send className="w-4 h-4 mr-1" />
                  {sending ? 'Envoi...' : 'Envoyer'}
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p>Sélectionnez une conversation</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
